import VueMultiselectExtended from '../inputs/multiselect';
import _u from '../../shared/utils';
import {InputMeta} from './custom-inputs-models';
import {disableHtmlScrollMixin} from '../mixins/component-mixins';
import Globalize from 'globalize';
import {globalActions} from '../../store/global-actions';
import {store} from '../../store/global-store';
import {updateModelValueEmit} from '../inputs/currency-price';

const updateIsActiveEmit = 'update:is-active';

const collapsedInputMixin = {
    mixins: [disableHtmlScrollMixin],
    props: {
        backgroundId: {},
    },

    data() {
        return {
            isActive: false,
        };
    },

    watch: {
        isActive(val) {
            this._toggleEditBackground(val);
        },
    },

    methods: {
        openEdit() {
            this.isActive = true;
        },

        closeEdit() {
            this.isActive = false;
        },

        _toggleEditBackground(show) {
            this._appendBackground();
            if (show && this.backgroundId && this.$refs.menu) {
                const bg = document.getElementById(this.backgroundId);
                if (bg) {
                    const rect = bg.getBoundingClientRect();
                    this.$refs.menu.style.minHeight = `${rect.y + rect.height}px`;
                }
            }
        },

        _appendBackground() {
            if (this.backgroundId) {
                const bg = document.getElementById(this.backgroundId);
                const backgroundContainer = this.$refs.background;
                if (bg && backgroundContainer) {
                    const bgCopy = bg.cloneNode(true);
                    bgCopy.id = '';
                    const scale = bg.getBoundingClientRect().height / bg.offsetHeight;
                    bgCopy.style.height = `${100 / scale}%`;
                    bgCopy.classList.add('content-overlay__bg-img');
                    while (backgroundContainer.hasChildNodes()) {
                        backgroundContainer.removeChild(backgroundContainer.lastChild);
                    }
                    backgroundContainer.appendChild(bgCopy);
                }
            }
        },
    },
};

const InputBase = {
    props: {
        modelValue: {},
        size: {},
    },
    data() {
        return {
            innerValue: this.modelValue,
        };
    },
    emits: [updateModelValueEmit],
    watch: {
        value() {
            this.innerValue = this.modelValue;
        },

        innerValue(val) {
            this.$emit(updateModelValueEmit, val);
        },
    },
};

const MetaInputBase = {
    props: {
        modelValue: InputMeta,
        size: String,
    },
    computed: {
        innerValue: {
            get() {
                return this.modelValue.value;
            },

            set(val) {
                this.modelValue.value = val;
            },
        },

        options() {
            return this.modelValue.options;
        },

        label() {
            return this.modelValue.label;
        },

        type() {
            return this.modelValue.type;
        },
    },
    methods: {
        getSummaryLabel(noValueLabel, withPrefix) {
            return this.modelValue.getSummaryLabel(noValueLabel, withPrefix);
        },
    },
};

const MultiselectBasedInput = {
    extends: MetaInputBase,
    computed: {
        formatter() {
            return this.modelValue.formatter;
        },
    },
    methods: {
        formatLabel(label, prefix) {
            const result = prefix ? `${prefix}: ${label}` : label;

            return this.formatter ? this.formatter(result) : result;
        },

        formatOption(option) {
            if (!option) {
                return null;
            }

            return this.formatLabel(option.label);
        },

        formatSelected(option, prefix) {
            if (!option) {
                return null;
            }

            return this.formatLabel(option.label, prefix || this.modelValue.label);
        },
    },
};

const addressInput = {
    extends: MetaInputBase,
    template: '#address-input',
    computed: {
        error() {
            return this.modelValue.error;
        },
    },
    data() {
        return {
            address: this.modelValue.value,
            isFullMode: false,
        };
    },
    watch: {
        'modelValue.value'(val) {
            this.address = val;
        },
    },
    methods: {
        switchToCompact() {
            if (this.isFullMode) {
                this._switchMode();
            }
        },

        switchToFull() {
            if (!this.isFullMode) {
                this._switchMode();
            }
        },

        _initAutocomplete() {
            store.subscribe((mutation) => {
                if (mutation.type === 'autocompleteCallback') {
                    if (mutation.payload) {
                        this.switchToCompact();
                    }
                }
            });
        },

        _switchMode() {
            globalActions.changeLogoVisibility(this.isFullMode);
            this.isFullMode = !this.isFullMode;
        },
    },
    mounted() {
        this._initAutocomplete();
    },
};

const rangeInput = {
    extends: MultiselectBasedInput,
    // NOTE: for now works for 0+ values only
    mixins: [collapsedInputMixin],
    template: '#range-input',
    components: {
        Multiselect: VueMultiselectExtended,
    },
    computed: {
        filteredMinOptions() {
            return this.innerValue && this.innerValue.max && this.innerValue.max.value ?
                this.options.filter((o) => o.value == null || o.value <= this.innerValue.max.value) :
                this.options;
        },
        filteredMaxOptions() {
            return this.innerValue && this.innerValue.min && this.innerValue.min.value ?
                this.options.filter((o) => o.value == null || o.value >= this.innerValue.min.value) :
                this.options;
        },
        minValue: {
            get() {
                return this.innerValue.min;
            },
            set(min) {
                this.innerValue = {min, max: this.innerValue.max};
            },
        },
        maxValue: {
            get() {
                return this.innerValue.max;
            },
            set(max) {
                this.innerValue = {min: this.innerValue.min, max};
            },
        },
    },
};

const checkGroupInput = {
    extends: MetaInputBase,
    mixins: [collapsedInputMixin],
    template: '#check-group-input',
    data() {
        return {
            id: _u.genUid(),
        };
    },
    methods: {
        getOptionId(key) {
            return `checkbox-${this.id}-${key}`;
        },

        triggerDeepUpdate() {
            // hack for deep update computed innerValue variable in MetaInputBase and InputMeta
            this.innerValue = this.innerValue;
        },

        _subscribeStore() {
            store.subscribe((mutation) => {
                if (mutation.type === 'filterDeepUpdate' && mutation.payload === this.modelValue._filterKey) {
                    this.triggerDeepUpdate();
                }
            });
        },
    },
    mounted() {
        this._subscribeStore();
    },
};

const extendedTextarea = {
    extends: InputBase,
    // TODO: size property is not bind to the nested elements
    template: '#extended-textarea',
    emits: ['submit', updateModelValueEmit],
    props: {
        buttonLabel: {},
        actionButtonId: {
            type: String,
            required: false,
        },
        isButtonAlwaysVisible: {default: false},
        isButtonActionable: {default: true},
        textareaClass: {},
        isAutoResized: {},
        buttonSize: String,
        textSize: String,
        hasEnterAction: {default: false},
        isIconButton: {default: false},
        iconClass: {},
        modelValue: String,
    },
    data() {
        return {
            initialValue: this.modelValue,
            isTextareaFocused: false,
        };
    },
    computed: {
        isButtonVisible() {
            return (this.buttonLabel || this.isIconButton) && (this.isDirty || this.isButtonAlwaysVisible);
        },

        isDirty() {
            return this.innerValue !== this.initialValue;
        },
    },
    watch: {
        innerValue(val) {
            if (val === '') {
                this.innerValue = null;
            }
        },
    },
    methods: {
        onButtonClick() {
            this._submit();
        },

        clear() {
            this.innerValue = this.initialValue;
        },

        focus() {
            this.$refs.textarea.focus();
        },

        _submit() {
            this.$emit('submit', this.innerValue);
            this.resetHeight();
        },

        _resize() {
            const el = this.$refs.textarea;
            const resize = () => {
                const scrollHeight = this.innerValue ? el.scrollHeight : this._initialScrollHeight;
                if (el.getBoundingClientRect().height !== scrollHeight) {
                    el.style.height = `${scrollHeight}px`;
                }
            };

            setTimeout(resize, 0);
        },

        resetHeight() {
            const el = this.$refs.textarea;
            el.style.height = `${this._initialScrollHeight}px`;
        },

        onFocus() {
            this.isTextareaFocused = true;
            store.commit('incrementActiveInputCount');
        },

        onBlur() {
            this.isTextareaFocused = false;
            store.commit('decrementActiveInputCount');
        },

        onEnter(e) {
            if (this.hasEnterAction) {
                e.preventDefault();
                this.$emit('enter');
            }
        },
    },

    mounted() {
        const textarea = this.$refs.textarea;
        this._initialScrollHeight = textarea.scrollHeight;
        textarea.addEventListener('keydown', this._resize);
    },
};

const multyselectSimple = {
    template: '#multiselect-simple',
    components: {
        Multiselect: VueMultiselectExtended,
    },
    props: {
        options: {required: true},
        trackBy: {default: 'value'},
        customLabel: {},
        searchable: {default: false},
        placeholder: {type: String},
        allowEmpty: {default: false},
        label: {default: 'label', type: String},
        modelValue: {},
        selectedValue: {},
        selectedLabel: {},
        noScroll: {},
        isActive: {type: Boolean},
        preselectLast: Boolean,
        preselectFirst: Boolean,
    },
    emits: [updateModelValueEmit, updateIsActiveEmit, 'update:selectedValue'],
    data() {
        return {
            selectedModel: this.modelValue,
        };
    },
    methods: {
        open() {
            this.$emit(updateIsActiveEmit, true);
        },
        close() {
            this.$emit(updateIsActiveEmit, false);
        },
        onChange(value) {
            this.$emit('change', value);
        },
    },
    computed: {
        innerSelectedValue: {
            get() {
                if (this.trackBy == null) {
                    return this.selectedModel;
                }

                if (this.selectedModel) {
                    return this.selectedModel[this.trackBy];
                }

                return null;
            },

            set(value) {
                if (value == null) {
                    this.selectedModel = null;
                }

                if (this.trackBy == null) {
                    this.selectedModel = this.options.find((option) => option == value);
                } else {
                    this.selectedModel = this.options.find((option) => option[this.trackBy] == value);
                }
            },
        },
    },
    watch: {
        selectedModel(value) {
            this.$emit(updateModelValueEmit, value);
            this.$emit('update:selectedValue', this.innerSelectedValue);
        },
        preselectLast: {
            handler(value) {
                if (value && !this.selectedValue) {
                    this.selectedModel = this.options && this.options.length > 0 ? this.options[this.options.length - 1] : null;
                }
            },

            immediate: true,
        },
        selectedValue(value) {
            this.innerSelectedValue = value;
        },
    },
    mounted() {
        if (this.selectedValue != null) {
            this.innerSelectedValue = this.selectedValue;
        }
    },
};

const citiesMultiselect = {
    template: '#cities-multiselect',
    components: {
        Multiselect: VueMultiselectExtended,
    },
    props: {
        trackBy: {default: 'value'},
        placeholder: {type: String},
        label: {default: 'label', type: String},
        cities: {type: Array, required: true},
    },
    emits: [],
    data() {
        return {
            selectedCity: this.modelValue,
            // For camelCase slot ('noResult' for this example)
            // Use dynamic slot names that do not contains uppercase letters
            // And put original slot name to variable of dynamic slot name
            // [GitHub Issue: https://github.com/shentao/vue-multiselect/issues/1692]
            //
            // DON'T USE SUCH NAME FOR YOUR VARIABLES
            no_result_slot_name: 'noResult',
        };
    },
    methods: {
        buildCityOptionUrl(value) {
            return `${window.location.origin}${value}`;
        },
    },
};

const citiesDropdownMultiselect = {
    template: '#cities-dropdown-multiselect',
    components: {
        Multiselect: VueMultiselectExtended,
    },
    props: {
        isBuyLocationMode: {default: true, required: false},
        trackBy: {default: 'value'},
        placeholder: {type: String},
        label: {default: 'label', type: String},
        selectedCityName: {type: String, required: false},
        cities: {type: Array, required: true},
    },
    data() {
        return {
            selectedCity: null,

            // For camelCase slot ('noResult' for this example)
            // Use dynamic slot names that do not contains uppercase letters
            // And put original slot name to variable of dynamic slot name
            // [GitHub Issue: https://github.com/shentao/vue-multiselect/issues/1692]
            //
            // DON'T USE SUCH NAME FOR YOUR VARIABLES
            no_result_slot_name: 'noResult',
        };
    },
    computed: {
        formattedSelectedCity() {
            return _u.changeWordForm(this.selectedCityName);
        },
    },
    methods: {
        initMultiselect() {
            this.$nextTick(() => {
                const multiselect = this.$refs.multiselect;

                multiselect.deactivate = () => { };
                multiselect.activate();

                this.setSelectedCityByLabel(multiselect, this.selectedCityName);
            });
        },
        setSelectedCityByLabel(multiselect, label) {
            this.selectedCity = multiselect.options.find((option) => option.label === label);
        },
        buildCityOptionUrl(value) {
            return `${window.location.origin}${value}`;
        },
    },
};

const invariantSeparator = '.';

const inputNumber = {
    template: '#input-number',
    props: {
        modelValue: {required: true},
        fieldName: {type: String},
        disabled: {type: Boolean},
        autocomplete: {type: String},
        placeholder: {type: String},
        allowEmpty: {type: Boolean},
        maxLength: {type: Number},
        roundFunc: {type: Function},
    },
    emits: [updateModelValueEmit],
    data() {
        return {
            decimalSeparator: '.',
            groupSeparator: ',',
            displayValue: this.modelValue,
        };
    },
    methods: {
        updateValue(value) {
            this.$emit(updateModelValueEmit, this._getUpdatedValue(value));
        },
        _getUpdatedValue(value) {
            let result = null;
            if (value == null || value === '') {
                this.displayValue = null;
            } else {
                result = this._parseValue(value);
            }

            return result;
        },
        _parseValue(value) {
            const invariantValue = this._getInvariantValue(value);

            const isFullValueParsed = invariantValue.search(/^\d+\.?\d*$/) === 0;
            if (isFullValueParsed) {
                const valueParts = invariantValue.split(invariantSeparator);
                valueParts[0] = this._formattedValue(valueParts[0]);
                this.displayValue = valueParts.join(this.decimalSeparator);
            }

            return this.displayValue;
        },
        _getInvariantValue(value) {
            const groupRegExp = new RegExp(this.groupSeparator, 'g');
            let invariantValue = value.toString()
                .replace(/\s/g, '')
                .replace(groupRegExp, '')
                .replace(this.decimalSeparator, invariantSeparator);
            if (invariantValue.startsWith(invariantSeparator)) {
                invariantValue = `0${invariantValue}`;
            }

            return invariantValue;
        },
        _roundValue(value) {
            if (this.roundFunc) {
                return this.roundFunc(value);
            }

            const decimalPartLength = 2;
            const roundMultiplier = Math.pow(10, decimalPartLength);
            return value != null ? Math.round(value * roundMultiplier) / roundMultiplier : null;
        },
        _formattedValue(value) {
            return (value != null && !isNaN(value)) ? _u.formatNumber(value) : value;
        },
        _initSeparators() {
            const decimalNumberWithGroups = 1000 + Math.random();
            const separators = Globalize.formatNumber(decimalNumberWithGroups).replace(/\d/g, '');
            if (separators.length > 1) {
                this.groupSeparator = separators[0];
            }
            const decimalSeparatorIndex = separators.length - 1;
            this.decimalSeparator = separators[decimalSeparatorIndex];
        },
    },
    mounted() {
        this._initSeparators();
        this.$emit(updateModelValueEmit, this._getUpdatedValue(this.modelValue));
    },
};

function initCustomInputsComponents(app) {
    app.component('dynamic-input', {
        extends: MetaInputBase,
        mixins: [collapsedInputMixin],
        template: '#dynamic-input',
    });

    app.component('address-input', addressInput);

    app.component('select-input', {
        extends: MultiselectBasedInput,
        template: '#select-input',
        components: {
            Multiselect: VueMultiselectExtended,
        },
    });

    app.component('multiselect-input', {
        extends: MultiselectBasedInput,
        template: '#multiselect-input',
        components: {
            Multiselect: VueMultiselectExtended,
        },
        methods: {
            limitText: (count) => `(+${count})`,
        },
    });

    app.component('range-input', rangeInput);

    app.component('check-input', {
        extends: MetaInputBase,
        template: '#check-input',
        computed: {
            id() {
                return this.modelValue ? this.modelValue.id : null;
            },
        },
    });

    app.component('check-group-input', checkGroupInput);

    app.component('filter-without-input', {
        extends: InputBase,
        render() {
            return null;
        },
    });

    app.component('extended-textarea', extendedTextarea);

    app.component('multiselect-simple', multyselectSimple);

    app.component('cities-multiselect', citiesMultiselect);

    app.component('cities-dropdown-multiselect', citiesDropdownMultiselect);

    app.component('input-number', inputNumber);
}

export default initCustomInputsComponents;
